import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import BackgroundDesign from '../components/BackgroundDesign';
import SEO from '../components/SEO';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faTimes, faNewspaper, faVideo, faImage, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
  padding: 2rem 0;
  margin-top: 80px;
  background: ${props => props.theme.colors.background};
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageHero = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.1)),
              url(${process.env.PUBLIC_URL}/images/media/media-hero.webp) center/cover no-repeat;
    filter: brightness(0.3);
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(to top, ${props => props.theme.colors.background}, transparent);
    z-index: 2;
  }
`;

const PageHeroContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 2rem;
  animation: ${fadeUp} 1s ease-out;
`;

const PageTitle = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  span {
    color: ${props => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  opacity: 0.9;
  margin-bottom: 2rem;
`;

const Section = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.background};
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  h2 {
    font-size: clamp(1.8rem, 4vw, 2.5rem);
    font-weight: 700;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;

    svg {
      color: ${props => props.theme.colors.primary};
    }
  }

  p {
    font-size: 1.1rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const FilterTab = styled.button`
  padding: 0.75rem 1.5rem;
  border: 2px solid ${props => props.active ? props.theme.colors.primary : '#ddd'};
  background: ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.colors.text};
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.active ? props.theme.colors.primary : 'rgba(153, 0, 17, 0.1)'};
  }
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MediaCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  animation: ${fadeUp} 0.6s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  }
`;

const MediaImageWrapper = styled.div`
  position: relative;
  aspect-ratio: 16/9;
  overflow: hidden;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  background: ${props => props.theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(153, 0, 17, 0.4);

  &:hover {
    transform: translate(-50%, -50%) scale(1.1);
    background: #b50013;
  }
`;

const MediaTypeBadge = styled.span`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: ${props => props.type === 'video' ? props.theme.colors.primary : props.theme.colors.secondary};
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const MediaContent = styled.div`
  padding: 1.5rem;
`;

const MediaTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.5rem;
`;

const MediaDate = styled.span`
  font-size: 0.85rem;
  color: #888;
  display: block;
  margin-bottom: 0.75rem;
`;

const MediaDescription = styled.p`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.6;
`;

const MediaSource = styled.span`
  display: inline-block;
  margin-top: 1rem;
  padding: 0.4rem 0.8rem;
  background: rgba(153, 0, 17, 0.1);
  color: ${props => props.theme.colors.primary};
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
`;

// Video Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  animation: ${fadeUp} 0.3s ease-out;
`;

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: ${props => props.platform === 'instagram' ? '400px' : '900px'};
  aspect-ratio: ${props => props.platform === 'instagram' ? '9/16' : '16/9'};
  max-height: 90vh;
  background: black;
  border-radius: 12px;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const VideoPlayer = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  outline: none;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -50px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

// Lightbox for images
const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  animation: ${fadeUp} 0.3s ease-out;
`;

const LightboxContent = styled.div`
  position: relative;
  max-width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
`;

const LightboxClose = styled.button`
  position: absolute;
  top: -3rem;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  transition: color 0.3s ease;
  z-index: 10;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const GalleryNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.direction === 'left' ? 'left: -4rem;' : 'right: -4rem;'}
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.2rem;

  &:hover {
    background: ${props => props.theme.colors.primary};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-50%) scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    ${props => props.direction === 'left' ? 'left: 1rem;' : 'right: 1rem;'}
    width: 40px;
    height: 40px;
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  top: -3rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const GalleryDots = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1.5rem;
  justify-content: center;
`;

const GalleryDot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? props.theme.colors.primary : 'rgba(255, 255, 255, 0.4)'};
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;

  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: scale(1.2);
  }
`;

const ImageCountBadge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  z-index: 10;
`;

// Sample Media Data - Replace with your actual data
const mediaData = [
  {
    id: 1,
    type: 'video',
    platform: 'instagram', // 'youtube', 'instagram', or 'local'
    title: "Salma Alichar's Interview with Jooj Media",
    date: 'December 2025',
    description: 'An inspiring conversation about TBH\'s mission and impact on communities across Morocco.',
    thumbnail: `${process.env.PUBLIC_URL}/images/media/jooj-interview-thumb.webp`,
    // For Instagram Reels: https://www.instagram.com/reel/REEL_ID/embed/
    // For Instagram Posts: https://www.instagram.com/p/POST_ID/embed/
    // For Vimeo: https://player.vimeo.com/video/VIDEO_ID (no query parameters)
    // For YouTube: https://www.youtube.com/embed/VIDEO_ID
    videoUrl: 'https://www.instagram.com/reel/DQ9tKRkiJPQ/embed/',
    source: 'Jooj Media'
  },
  // Example: Single image (string format)
  // {
  //   id: 5,
  //   type: 'screenshot',
  //   title: 'Single Image Example',
  //   date: 'June 2023',
  //   description: 'Example with a single image.',
  //   image: `${process.env.PUBLIC_URL}/images/media/single-image.webp`,
  //   source: 'Source Name'
  // },

  // Example: Multiple images (array format) - Gallery will show navigation
  {
    id: 5,
    type: 'screenshot',
    title: 'TBH x Agence MAP',
    date: 'June 2023',
    description: 'Photo gallery from our press coverage with Agence MAP.',
    image: [
      `${process.env.PUBLIC_URL}/images/media/agence-map.webp`,
      `${process.env.PUBLIC_URL}/images/media/beach-morocco/beach-cleaning01.webp`,
      `${process.env.PUBLIC_URL}/images/media/beach-morocco/beach-cleaning02.webp`,
      `${process.env.PUBLIC_URL}/images/media/beach-morocco/beach-cleaning03.webp`,
      `${process.env.PUBLIC_URL}/images/media/beach-morocco/beach-cleaning04.webp`,
    ],
    source: 'TBH Media'
  },
  {
    id: 6,
    type: 'video',
    platform: 'vimeo',
    title: 'TBH x AlAoula TV',
    date: 'August 11, 2023',
    description: 'A short clip from our recent community outreach program.',
    thumbnail: `${process.env.PUBLIC_URL}/images/media/alaoula-tv.webp`,
    videoUrl: "https://player.vimeo.com/video/1162316063",
    source: 'TBH Media'
  },
  {
    id: 7,
    type: 'video',
    platform: 'vimeo',
    title: 'TBH x 2M TV - M24',
    date: 'August 11, 2023',
    description: 'A short clip from our recent community outreach program.',
    thumbnail: `${process.env.PUBLIC_URL}/images/media/2m-tv.webp`,
    videoUrl: "https://player.vimeo.com/video/1162316105",
    source: 'TBH Media'
  },
  // {
  //   id: 2,
  //   type: 'screenshot',
  //   title: 'Featured on Morocco News',
  //   date: 'November 2024',
  //   description: 'TBH was featured in a national coverage highlighting our recent humanitarian initiatives.',
  //   image: `${process.env.PUBLIC_URL}/images/media/news-feature-1.jpg`,
  //   source: 'Morocco News'
  // }
  // Add more media items as needed
];

const MediaAppearancesPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedImageGallery, setSelectedImageGallery] = useState(null); // Store array or single image
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const filteredMedia = activeFilter === 'all'
    ? mediaData
    : mediaData.filter(item => item.type === activeFilter);

  const handleMediaClick = (item) => {
    if (item.type === 'video') {
      setSelectedVideo(item);
    } else {
      // Support both single image and array of images
      const images = Array.isArray(item.image) ? item.image : [item.image];
      setSelectedImageGallery(images);
      setCurrentImageIndex(0);
    }
  };

  const handleCloseLightbox = () => {
    setSelectedImageGallery(null);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    if (selectedImageGallery && currentImageIndex < selectedImageGallery.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const handlePrevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const handleDotClick = (index) => {
    setCurrentImageIndex(index);
  };

  // Helper function to get the display image (first image if array)
  const getDisplayImage = (item) => {
    return Array.isArray(item.image) ? item.image[0] : item.image;
  };

  // Helper function to get image count
  const getImageCount = (item) => {
    return Array.isArray(item.image) ? item.image.length : 1;
  };

  return (
    <PageWrapper>
      <BackgroundDesign />
      <SEO
        title="In The Spotlight | The Big Heart Association Media Appearances"
        description="Discover The Big Heart Association's media features, press coverage, and interviews. See how TBH Morocco is making headlines through its humanitarian and social impact work."
        url="/media"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Media", url: "/media" }]}
        noOrganizationSchema
      />

      <PageHero>
        <PageHeroContent>
          <PageTitle>
            In The <span>Spotlight</span>
          </PageTitle>
          <PageSubtitle>
            Discover TBH's media presence and press features. From interviews to news coverage,
            explore how our mission is making headlines and inspiring change.
          </PageSubtitle>
        </PageHeroContent>
      </PageHero>

      <MainContent>
        <Section>
          <SectionContainer>
            <SectionHeader>
              <h2>
                <FontAwesomeIcon icon={faNewspaper} />
                Media Features & Appearances
              </h2>
              <p>
                Our journey captured through interviews, news coverage, and social media highlights.
              </p>
            </SectionHeader>

            <FilterTabs>
              <FilterTab
                active={activeFilter === 'all'}
                onClick={() => setActiveFilter('all')}
              >
                All Media
              </FilterTab>
              <FilterTab
                active={activeFilter === 'video'}
                onClick={() => setActiveFilter('video')}
              >
                <FontAwesomeIcon icon={faVideo} />
                Videos
              </FilterTab>
              <FilterTab
                active={activeFilter === 'screenshot'}
                onClick={() => setActiveFilter('screenshot')}
              >
                <FontAwesomeIcon icon={faImage} />
                Press
              </FilterTab>
            </FilterTabs>

            <MediaGrid>
              {filteredMedia.map((item, index) => (
                <MediaCard key={item.id} delay={`${index * 0.1}s`}>
                  <MediaImageWrapper onClick={() => handleMediaClick(item)}>
                    <img
                      src={item.type === 'video' ? item.thumbnail : getDisplayImage(item)}
                      alt={item.title}
                      onError={(e) => {
                        // Prevent infinite loop by checking if already set to placeholder
                        if (!e.target.dataset.fallback) {
                          e.target.dataset.fallback = 'true';
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="225" viewBox="0 0 400 225"><rect fill="%23ddd" width="400" height="225"/><text fill="%23999" font-family="Arial" font-size="16" x="50%" y="50%" text-anchor="middle" dy=".3em">Image Not Available</text></svg>';
                        }
                      }}
                    />
                    <MediaTypeBadge type={item.type}>
                      <FontAwesomeIcon icon={item.type === 'video' ? faVideo : faImage} />
                      {item.type === 'video' ? 'Video' : 'Press'}
                    </MediaTypeBadge>
                    {item.type === 'video' && (
                      <PlayButton>
                        <FontAwesomeIcon icon={faPlay} />
                      </PlayButton>
                    )}
                    {item.type === 'screenshot' && getImageCount(item) > 1 && (
                      <ImageCountBadge>
                        <FontAwesomeIcon icon={faImage} />
                        {getImageCount(item)}
                      </ImageCountBadge>
                    )}
                  </MediaImageWrapper>
                  <MediaContent>
                    <MediaTitle>{item.title}</MediaTitle>
                    <MediaDate>{item.date}</MediaDate>
                    <MediaDescription>{item.description}</MediaDescription>
                    <MediaSource>{item.source}</MediaSource>
                  </MediaContent>
                </MediaCard>
              ))}
            </MediaGrid>
          </SectionContainer>
        </Section>
      </MainContent>

      {/* Video Modal */}
      {selectedVideo && (
        <ModalOverlay onClick={() => setSelectedVideo(null)}>
          <ModalContent
            platform={selectedVideo.platform || 'youtube'}
            onClick={(e) => e.stopPropagation()}
          >
            <CloseButton onClick={() => setSelectedVideo(null)}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
            {selectedVideo.platform === 'local' ? (
              <VideoPlayer
                controls
                autoPlay
                src={selectedVideo.videoUrl}
                type="video/mp4"
              >
                Your browser does not support the video tag.
              </VideoPlayer>
            ) : (
              <iframe
                src={selectedVideo.videoUrl}
                title={selectedVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Image Gallery Lightbox */}
      {selectedImageGallery && (
        <LightboxOverlay onClick={handleCloseLightbox}>
          <LightboxContent onClick={(e) => e.stopPropagation()}>
            <LightboxClose onClick={handleCloseLightbox}>
              <FontAwesomeIcon icon={faTimes} />
            </LightboxClose>

            {selectedImageGallery.length > 1 && (
              <ImageCounter>
                {currentImageIndex + 1} / {selectedImageGallery.length}
              </ImageCounter>
            )}

            {selectedImageGallery.length > 1 && (
              <GalleryNav
                direction="left"
                onClick={handlePrevImage}
                disabled={currentImageIndex === 0}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </GalleryNav>
            )}

            <LightboxImage
              src={selectedImageGallery[currentImageIndex]}
              alt={`Image ${currentImageIndex + 1}`}
            />

            {selectedImageGallery.length > 1 && (
              <GalleryNav
                direction="right"
                onClick={handleNextImage}
                disabled={currentImageIndex === selectedImageGallery.length - 1}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </GalleryNav>
            )}

            {selectedImageGallery.length > 1 && (
              <GalleryDots>
                {selectedImageGallery.map((_, index) => (
                  <GalleryDot
                    key={index}
                    active={index === currentImageIndex}
                    onClick={() => handleDotClick(index)}
                  />
                ))}
              </GalleryDots>
            )}
          </LightboxContent>
        </LightboxOverlay>
      )}
    </PageWrapper>
  );
};

export default MediaAppearancesPage;
